import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import Handsontable from 'handsontable';
import {PricingArea} from '../data/pricing-area';
import {PricingGroup} from '../data/group/pricing-group';
import {ProjectCalcService} from '../data/project-calc.service';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, filter} from 'rxjs/operators';
import {ColumnSettings} from 'handsontable/settings';
import {CellChange} from 'handsontable/common';
import {PricingGroupItem} from '../data/group/pricing-group-item';
import {
  MiscItemFilesDialogComponent
} from '../../admin-panel/view/item-editor/misc-item-files-dialog/misc-item-files-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmationService} from 'primeng/api';
import {HyperFormula} from 'hyperformula';
import moment from 'moment/moment';

class UpdateQueueAction {
  action: string;
  data: PricingGroup
}

@Component({
  selector: 'app-table-sheet',
  templateUrl: './table-sheet.component.html',
  styleUrls: ['./table-sheet.component.scss']
})
export class TableSheetComponent implements OnInit, AfterViewInit, OnDestroy {
  componentWidth = 24;

  @HostBinding('class.dropdown-table')
  dropDownTable = false;

  @Input()
  pricingName: string

  @Input()
  listIsEditable = true;

  @Input()
  pricingGroup: PricingGroup

  @Input()
  allGroups: PricingGroup[]

  @Input()
  area: PricingArea;

  @Input()
  pricingId: number

  @ViewChild('table')
  tableEl: ElementRef;

  table: Handsontable;

  updateSubject = new Subject();

  subToChangesSubj: Subscription;

  @Input()
  typeOfSheet: 'Default' | 'Custom' | 'DropDown' | 'Notes';

  tableWithCustomFields = false;

  customTableLoading = false;

  updateQueue: UpdateQueueAction[] = [];

  constructor(
    private dialog: MatDialog,
    protected confirmationService: ConfirmationService,
    private elementRef: ElementRef,
    private projectCalcService: ProjectCalcService,
    private appRef: ApplicationRef
  ) {
  }

  ngOnInit(): void {
    this.updateSubject.pipe(
      debounceTime(50)
    ).subscribe(() => {
      if (this.typeOfSheet === 'Notes') {
        this.updateArea()
      } else {
        this.updateGroup()
      }
    })

    if (this.pricingGroup && this.pricingGroup.name === 'Other') {
      this.typeOfSheet = 'Custom'

    } else if (this.pricingGroup && this.pricingGroup.pricingCategory && (this.pricingGroup.pricingCategory.categoryType === 'MISC' ||
      this.pricingGroup.pricingCategory.categoryType === 'ADDONS')) {
      this.typeOfSheet = 'DropDown'
      this.dropDownTable = true

      this.componentWidth = 36

      if (this.pricingGroup.pricingCategory.categoryType === 'ADDONS') {
        this.listIsEditable = false;

        this.dropDownTable = false
      }
    } else {
      if (this.pricingGroup.pricingCategory.categoryType === 'UPGRADES') {
        this.componentWidth = 36
        this.typeOfSheet = 'Notes'
      }
    }

    this.subToChanges()
  }

  // vol2
  subToChanges() {
    this.subToChangesSubj = this.projectCalcService.groupToUpdate.pipe(
      filter(info => info.entity?.group?.id === this.pricingGroup?.id)
    ).subscribe(updateInfo => {
      this.updateQueue.push({
        action: updateInfo.action,
        data: updateInfo.entity
      })

      this.updateAll();
    })
  }

  updateAll() {
    while (this.updateQueue.length > 0) {
      const update = this.updateQueue.shift()
      const updatedAction = update.action
      const updatedGroup = update.data

      if (updatedAction === 'CREATE') {
        this.createRows(updatedGroup)
      }
      if (updatedAction === 'UPDATE') {
        this.updateRows(updatedGroup)
      }
      if (updatedAction === 'DELETE') {
        this.deleteRows(updatedGroup)
      }
    }
  }

  updateRows(entity: PricingGroupItem) {
    const fitem = this.pricingGroup.items.find(item => item.id === entity.id);

    if (!fitem) {
      return;
    }

    const diffSeconds = moment(entity.lastInteractionTime).diff(fitem.lastInteractionTime, 'seconds')

    if (diffSeconds < 0) {
      return;
    }

    if (entity.qty && entity.qty >= 0) fitem.qty = entity.qty
    if (entity.width && entity.width >= 0) fitem.width = entity.width
    if (entity.depth && entity.depth >= 0) fitem.depth = entity.depth
    if (entity.height && entity.height >= 0) fitem.height = entity.height
    if (entity.name) fitem.name = entity.name
    if (entity.note) fitem.note = entity.note
    if (entity.price && entity.price >= 0) fitem.price = entity.price
    if (entity.pos && entity.pos >= 0) fitem.pos = entity.pos
    if (entity.customFields) fitem.customFields = entity.customFields

    if (entity.misc) {
      fitem.misc = entity.misc
    }

    this.pricingGroup.items.forEach(item => {
      item.customFields.forEach(field => {
        item[field.modifier.name] = field.value
      })
    })

    this.table.updateData(this.pricingGroup.items)
    this.updateCustomFields()
  }

  createRows(entity: PricingGroupItem) {
    if (!this.pricingGroup.items.find(item => item.id === entity.id)) {
      const newItem = Object.assign({}, entity)
      this.pricingGroup.items.push(newItem)
      this.table.updateData(this.pricingGroup.items)
      this.updateCustomFields()
    }
  }


  deleteRows(entity: PricingGroupItem) {
    const fitem = this.pricingGroup.items.find(item => item.id === entity.id);
    if (fitem) {
      const index = this.pricingGroup.items.indexOf(fitem);
      this.pricingGroup.items.splice(index, 1);
      this.table.updateData(this.pricingGroup.items)
    }
  }

  ngAfterViewInit() {
    if (this.typeOfSheet === 'Default') {
      this.fillDefaultTable()
    } else if (this.typeOfSheet === 'Custom') {
      this.fillCustomTable()
    } else if (this.typeOfSheet === 'DropDown') {
      this.fillDropDownTable()
    } else if (this.typeOfSheet === 'Notes') {
      this.fillNotesTable()
    }
  }


  fillDefaultTable(customColumns?, customHeaders?, customColWidths?, height = 'auto', contextMenu = true) {
    let columns = [
      {data: 'width', type: 'numeric'},
      {data: 'height', type: 'numeric'},
      {data: 'qty', type: 'numeric'},
    ] as ColumnSettings[]

    let colHeaders = ['Width', 'Height', 'QTY'];
    let colWidths = [50, 50, 50]

    if (customColumns) {
      columns = customColumns
      colHeaders = customHeaders
      colWidths = customColWidths
    }


    // // CUSTOM FIELDS / CHECKMARKS
    if (this.pricingGroup.items && this.pricingGroup.items[0] && this.pricingGroup.items[0].customFields.length) {
      this.tableWithCustomFields = true;

      this.pricingGroup.items.forEach(item => {
        item.customFields.forEach(field => {
          item[field.modifier.name] = field.value
        })
      })

      let customFieldsLengths = 0
      this.pricingGroup.items[0].customFields.forEach(field => {
        columns.push(
          {data: field.modifier.name, type: 'checkbox', className: 'htCenter'})

        colHeaders.push(field.modifier.name)
        const columnLength = (field.modifier.name.length * 5) + 20
        colWidths.push(columnLength)
        customFieldsLengths += columnLength
      })
    }


    // // PANELS table depth instead of width
    if (this.pricingName === 'Thick Panels') {
      columns[0].data = 'depth'
      columns[1].data = 'height'
      colHeaders[0] = 'Depth'
      colHeaders[1] = 'Height'
    }

    if (this.pricingName === 'Panels') {
      columns[0].data = 'depth'
      columns[1].data = 'height'
      colHeaders[0] = 'Depth'
      colHeaders[1] = 'Height'
    }

    if (this.pricingName === 'LV Panels') {
      columns[0].data = 'width'
      columns[1].data = 'depth'
      colHeaders[0] = 'Width'
      colHeaders[1] = 'Depth'
    }

    if (this.pricingName === 'Wall Shelves') {
      columns[0].data = 'width'
      columns[1].data = 'depth'
      colHeaders[0] = 'Width'
      colHeaders[1] = 'Depth'
    }


    // Adding note field to each table as last cell
    if (this.pricingGroup.name !== 'Other') {
      columns.push({data: 'note', type: 'text'})
      colHeaders.push('Note')

      let min = 150;

      this.pricingGroup.items?.forEach(item => {
        if (item.note) {
          min = Math.max(min, item.note.length * 5)
        }
      })

      colWidths.push(min)
    }


    this.makeCellYellow(columns)

    this.table = new Handsontable(this.tableEl.nativeElement, {
      data: this.pricingGroup.items,
      height,
      rowHeaders: true,
      rowHeaderWidth: 30,
      colHeaders,
      columns,
      colWidths,
      licenseKey: 'non-commercial-and-evaluation', // for non-commercial use only
      contextMenu,
      afterOnCellMouseUp() {
        const activeEditor = this.getActiveEditor();
        if (!activeEditor) {
          return;
        }

        activeEditor.TEXTAREA?.setAttribute('maxlength', 65)
      },
      afterChange: (changes) => {
        if (changes) {
          const row = changes[0][0]
          const col = this.table.propToCol(changes[0][1] as string) as number;
          const colName = changes[0][1]
          const val = changes[0][3]

          if (this.typeOfSheet === 'DropDown') {
            if (colName === 'misc.name' && !this.projectCalcService.miscItemsMap.has(val)) {
              this.createNewMiscItem(row, col, val)
              return;
            }
            this.dropDownDataChanged(changes)
          }

          this.updateCustomFields()

          // update quantity
          if (colName === 'width') {
            const qtyCol = this.table.propToCol('qty') as number;
            changes.forEach(change => {
              const singleRow = change[0]
              const newVal = change[3]
              if (newVal > 0) {
                this.table.setDataAtCell(singleRow, qtyCol, 1)
              } else if (newVal === 0) {
                this.table.setDataAtCell(singleRow, qtyCol, 0)
              }
            })
          }

          if (row === this.pricingGroup.items.length - 1 && this.typeOfSheet !== 'DropDown' && typeof changes[0][3] !== 'boolean') {
            this.addNewLine()
            this.table.selectCell(row + 1, col)
          }

          const timeStamp = moment().format('YYYY-MM-DDTHH:mm:ss');
          this.pricingGroup.items[row].lastInteractionTime = timeStamp

          this.updateSubject.next()
        }
      },
      afterCreateRow: (newChild) => {
        if (newChild >= 0) {
          this.updateSubject.next()
        }
      },
      beforeRemoveRow: (removedIndex: number) => {
        if (removedIndex >= 0) {
          this.updateSubject.next()
        }
      },
      afterSelectionEnd: () => {
        this.countSelectedCells()
      },
      outsideClickDeselects: () => {
        this.countSelectedCells()
        return true;
      }
    });
  }

  createNewMiscItem(row, col, newMiscName: string) {
    this.confirmationService.confirm({
      header: 'Misc Item Not Found',
      message: `Misc item not found, would you like to create new?`,
      dismissableMask: true,
      icon: 'pi pi-plus',
      accept: () => {
        const dialogRef = this.dialog.open(MiscItemFilesDialogComponent, {
          data: {
            misc: null,
            newItemName: newMiscName
          },
          panelClass: 'misc-item-files-dialog'
        });

        dialogRef.afterClosed().subscribe(newMisc => {
          if (newMisc) {
            this.projectCalcService.miscItems.push(newMisc)
            this.projectCalcService.miscItemsMap.set(newMisc.name, newMisc)
            this.projectCalcService.miscItemsArr.push(newMisc.name)

            this.table.setDataAtCell(row, col, newMisc.name)
          }
        })
      }
    });

  }

  makeCellYellow(columns: ColumnSettings[]) {
    if (this.pricingGroup.name === 'Lower Cabinets' ||
      this.pricingGroup.name === 'Upper Cabinets' ||
      this.pricingGroup.name === 'Tall Cabinets' ||
      this.pricingGroup.name === 'Open Cabinets' ||
      this.pricingGroup.name === 'Wall Cabinets') {
      columns[0].className = 'htEditable'; // Making width cells yellow background
    } else if (this.pricingGroup.name === 'Addons') {
      columns[1].className = 'htEditable' // Making qty cells yellow background
    }
  }

  updateCustomFields() {
    if (this.tableWithCustomFields) {
      this.pricingGroup.items.forEach(item => {
        item.customFields.forEach(field => {
          field.value = item[field.modifier.name]
        })
      })
    }
  }

  fillCustomTable() {
    const customColumns = [
      {data: 'name', type: 'text'},
      {data: 'qty', type: 'numeric'}
    ] as ColumnSettings[]

    const customHeaders = ['Name', 'QTY']
    const customColWidths = [200, 50]

    this.fillDefaultTable(customColumns, customHeaders, customColWidths)
  }

  fillNotesTable() {
    let data
    if (this.area.jsonData) {
      data = JSON.parse(this.area.jsonData)
    } else {
      data = [
        ['', '', '', ''],
        ['', '', '', ''],
        ['', '', '', '']
      ]
    }
    this.table = new Handsontable(this.tableEl.nativeElement, {
      data,
      // data: this.area.jsonData,
      formulas: {
        engine: HyperFormula,
      },
      beforeOnCellMouseDown(event, coords, TD, controller) {
        const activeEditor = this.getActiveEditor();

        if (!activeEditor) {
          return;
        }
        if (!activeEditor.isOpened()) {
          return;
        }
        if (event.target === activeEditor.TEXTAREA) {
          return;
        }

        if (!activeEditor.TEXTAREA.value) {
          return
        }
        if (activeEditor.TEXTAREA.value && activeEditor.TEXTAREA.value[0] !== '=') {
          return
        }


        const {TEXTAREA} = activeEditor;

        controller.cell = true;

        const spreadsheetAddress = `${Handsontable.helper.spreadsheetColumnLabel(coords.col)}${coords.row + 1}`;
        activeEditor.TEXTAREA.value += spreadsheetAddress

        activeEditor.TEXTAREA.style.width = activeEditor.TEXTAREA.getBoundingClientRect().width + 5 + 'px'

      },
      afterOnCellMouseUp() {
        const activeEditor = this.getActiveEditor();

        if (!activeEditor) {
          return;
        }
        if (!activeEditor.isOpened()) {
          return;
        }
        if (event.target === activeEditor.TEXTAREA) {
          return;
        }

        activeEditor.focus();
      },
      maxCols: 8,
      height: 'auto',
      rowHeaders: true,
      colHeaders: true,
      manualColumnResize: true,

      rowHeaderWidth: 30,
      licenseKey: 'non-commercial-and-evaluation', // for non-commercial use only
      contextMenu: true,
      afterChange: (changes) => {
        if (changes) {
          this.area.jsonData = JSON.stringify(this.table.getSourceData())
          this.updateSubject.next()
        }
      },
      afterSelectionEnd: () => {
        this.countSelectedCells()
      },
      outsideClickDeselects: () => {
        this.countSelectedCells()
        return true;
      }
    });
  }

  fillDropDownTable() {
    const customHeaders = ['Name', 'QTY', 'Price']
    const customColWidths = [200, 50, 50]
    let height: any = 25;
    let contextMenu = true;

    height = Math.max(height, this.pricingGroup.items.length * 2)
    height += 'em'

    const customColumns = [
      {
        data: 'misc.name',
        type: 'autocomplete',
        strict: true,
        filter: true,
        source: this.projectCalcService.miscItemsArr,
      },
      {data: 'qty', type: 'numeric'},
      {data: 'price', type: 'numeric'}
    ] as ColumnSettings[]

    if (this.pricingGroup.pricingCategory.categoryType === 'ADDONS') {
      customColumns[0].type = 'text';
      customColumns[0].readOnly = true;
      customColumns[0].data = 'name';
      height = 'auto'
      contextMenu = false;
    }


    this.fillDefaultTable(customColumns, customHeaders, customColWidths, height, contextMenu)

    if (this.pricingGroup?.name === 'Addons') {
      this.updateAddonsCells()
    }
  }

  updateAddonsCells() {
    this.table.updateSettings({
      cells: (row, col, prop) => {
        const cellProperties = {} as ColumnSettings;

        if (prop === 'qty') {
          const fieldName = this.table.getDataAtCell(row, 0)
          if (fieldName === 'Total Drawer/Door Faces' || fieldName === 'Design' || fieldName === 'Rounds of Drawings' || fieldName === 'Project Management') {
            cellProperties.readOnly = true
            cellProperties.className = ''
          }
        }

        return cellProperties;
      }
    })
  }

  countSelectedCells() {
    this.projectCalcService.countSelectedCells(this.table)
  }

  addNewLine() {
    this.table.alter('insert_row_below')

    if (this.typeOfSheet === 'Notes') {
      return
    }

    const lastCellIndex = this.pricingGroup.items.length - 1

    if (!this.dropDownTable) {
      this.pricingGroup.items[lastCellIndex].height = 0
      this.pricingGroup.items[lastCellIndex].width = 0
      this.pricingGroup.items[lastCellIndex].depth = 0
      this.pricingGroup.items[lastCellIndex].qty = 0

      if (this.pricingGroup.items.length > 1) {
        this.pricingGroup.items[lastCellIndex].height = this.pricingGroup.items[lastCellIndex - 1].height
      }
    } else {
      const hotCell = this.table.getCell(lastCellIndex, 0);

      hotCell.dispatchEvent(new MouseEvent('mousedown', {bubbles: true}));

      this.appRef.tick()

      hotCell.dispatchEvent(new MouseEvent('mouseup', {bubbles: true}))
      this.pricingGroup.items[lastCellIndex].qty = 1

    }

  }

  updateGroup() {
    const timeStamp = moment().format('YYYY-MM-DDTHH:mm:ss');
    this.pricingGroup.lastInteractionTime = timeStamp

    this.projectCalcService.updateGroup(this.pricingId, this.area.id, this.pricingGroup).subscribe(newGroup => {

      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < newGroup.items.length; i++) {
        if (this.pricingGroup.items[i] && newGroup.items[i]) {
          this.pricingGroup.items[i].id = newGroup.items[i].id
          this.pricingGroup.items[i].misc = newGroup.items[i].misc
          this.pricingGroup.items[i].price = newGroup.items[i].price

          // TODO TEST
          this.pricingGroup.items[i].customFields = newGroup.items[i].customFields
        }
      }

      // TODO TEST
      this.updateCustomFields()

      this.projectCalcService.emitAllUpdates(this.area.id)
      this.table.render()
    })
  }

  updateArea() {
    this.projectCalcService.updateArea(this.pricingId, {
      id: this.area.id,
      jsonData: this.area.jsonData
    }).subscribe(() => {
    })
  }

  dropDownDataChanged(changes: CellChange[]) {
    const row = changes[0][0]
    const colName = changes[0][1]
    const newVal = changes[0][3]
    // const col = this.table.propToCol('price')

    if (colName === 'misc.name' && this.projectCalcService.miscItemsMap.has(newVal)) {
      this.pricingGroup.items[row].misc = this.projectCalcService.miscItemsMap.get(newVal)
      // this.table.setDataAtCell(row, col, this.projectCalcService.miscItemsMap.get(newVal).pricePerItem)
    }
  }

  ngOnDestroy() {
    if (this.subToChangesSubj) {
      this.subToChangesSubj.unsubscribe()
      this.subToChangesSubj = null;
    }
  }
}
